<template>
  <div>
    <v-menu
      offset-y
      nudge-bottom="8"
      :close-on-content-click="false"
      content-class="rounded-lg"
      transition="slide-y-transition"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          v-bind="attrs"
          v-on="on"
          style="cursor: pointer"
          class="mx-5"
          @click="ocultarBadge"
        >
          <v-badge
            offset-x="10"
            offset-y="10"
            color="green"
            :content="badgeCount"
            :value="badgeCount"
          >
            <v-icon>mdi-bell</v-icon>
          </v-badge>
        </span>
      </template>

      <v-card class="notif-panel" flat>
        <!-- Cabecera: la marca entra por el contador en primary (el tema lo
             baja getEmpresaConfig desde el back), no por un bloque de color. -->
        <div class="d-flex align-center px-4 pt-4 pb-2">
          <span class="text-subtitle-1 font-weight-bold">Notificaciones</span>
          <v-spacer></v-spacer>
          <span v-if="totalNoLeidas" class="text-caption primary--text font-weight-medium">
            {{ totalNoLeidas }} sin leer
          </span>
        </div>

        <!-- Publicidad: novedad de la plataforma al tope, solo si hay campaña -->
        <NovedadCampana />

        <!-- Loading state -->
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="28" />
        </div>

        <!-- Empty state -->
        <div v-else-if="items.length === 0" class="text-center pa-8">
          <v-icon size="40" color="grey lighten-1">mdi-bell-outline</v-icon>
          <div class="text-body-2 font-weight-medium mt-2">Todo al día</div>
          <div class="text-caption text--secondary">
            No hay notificaciones nuevas
          </div>
        </div>

        <!-- Notification list: tarjetas separadas, sin divisores. -->
        <div v-else class="px-2 pb-2 overflow-y-auto notif-list">
          <div
            v-for="item in items"
            :key="item.id_notificacion"
            class="d-flex rounded overflow-hidden mb-1 notif-item"
            :class="{ 'notif-item--unread': !item.visto }"
          >
            <!-- Franja de severidad: clase de color de Vuetify, no hex. -->
            <div :class="item.type || 'info'" class="notif-item__accent"></div>

            <div class="d-flex align-start flex-grow-1 pa-2" style="min-width: 0">
              <div
                class="flex-grow-1"
                style="min-width: 0"
                :style="item.url ? 'cursor: pointer' : ''"
                @click="descargarNotificacion(item)"
              >
                <div class="d-flex align-baseline">
                  <!-- Sin leer = titulo en negrita. -->
                  <span
                    class="text-body-2 flex-grow-1"
                    :class="item.visto ? 'font-weight-regular' : 'font-weight-bold'"
                  >
                    {{ item.titulo }}
                  </span>
                  <span class="text-caption text--disabled ml-2 flex-shrink-0">
                    {{ formatDate(item.createdAt) }}
                  </span>
                </div>
                <div class="text-caption text--secondary">
                  {{ item.mensaje }}
                </div>
              </div>

              <v-btn
                icon
                x-small
                class="ml-1"
                aria-label="Eliminar notificación"
                @click.stop="deleteNotificacion(item.id_notificacion)"
              >
                <v-icon size="16" color="grey lighten-1">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import moment from "moment";
import socket from "@/composables/conexionSocket.js";
import { abrirNotificacionUrl } from "@/utils/notificacionAccion";
import NovedadCampana from "../publicidad/NovedadCampana.vue";

export default {
  name: "NotificationBadgeDashboard",
  components: { NovedadCampana },
  data() {
    return {
      dataUsuario: null,
      // Purely visual dismissal — hides the numeric badge on click without
      // marking anything as read server-side. Reopens when new unread
      // notifications arrive (see watch.totalNoLeidas).
      badgeDismissed: false,
    };
  },
  async created() {
    this.dataUsuario = JSON.parse(localStorage.getItem("token"));
    if (!this.dataUsuario) {
      console.error("No se encontró 'dataUsuario' en el almacenamiento local.");
      return;
    }

    // Register the socket listener for incoming notifications.
    // Uses a named method so it can be removed in beforeDestroy.
    socket.on("notificacionUsuario", this.handleIncomingNotification);

    // Re-sync on every (re)connect: a notification emitted while the socket was
    // disconnected (e.g. during a long async report generation) never arrives
    // live — socket.io doesn't replay it. Re-fetching on reconnect surfaces it
    // without a manual page reload.
    socket.on("connect", this.fetchNotificaciones);

    await this.fetchNotificaciones();
  },
  beforeDestroy() {
    socket.off("notificacionUsuario", this.handleIncomingNotification);
    socket.off("connect", this.fetchNotificaciones);
  },
  computed: {
    fechaDelDia() {
      return moment().format("DD-MM-YY");
    },
    items() {
      return this.$store.state.notificaciones.bellItems;
    },
    loading() {
      return this.$store.state.notificaciones.bellLoading;
    },
    totalNoLeidas() {
      return this.$store.state.notificaciones.totalNoLeidas;
    },
    // La novedad de la plataforma suma 1 al contador mientras está visible;
    // no toca el store de notificaciones del comercio.
    hayNovedadPlataforma() {
      const porTipo = (this.$store.getters && this.$store.getters["publicidad/porTipo"]) || {};
      return Boolean(porTipo.campana);
    },
    badgeCount() {
      if (this.badgeDismissed) return 0;
      return this.totalNoLeidas + (this.hayNovedadPlataforma ? 1 : 0);
    },
  },
  watch: {
    // Only re-arms the dismissed badge when unread COUNT goes up (a new
    // notification arrived) — a decrease (mark read/delete) must not
    // resurrect a badge the user already dismissed.
    totalNoLeidas(newVal, oldVal) {
      if (newVal > oldVal) {
        this.badgeDismissed = false;
      }
    },
    // Una novedad nueva de la plataforma también vuelve a prender el contador
    hayNovedadPlataforma(ahora, antes) {
      if (ahora && !antes) {
        this.badgeDismissed = false;
      }
    },
  },
  methods: {
    async fetchNotificaciones() {
      if (!this.dataUsuario) return;
      try {
        await this.$store.dispatch("notificaciones/fetchBellNotificaciones");
      } catch (error) {
        console.error("Error al obtener notificaciones:", error);
      }
    },
    // Live 'notificacionUsuario' socket event — delegates the actual
    // filtering/state update to the shared Vuex module (SC-26, SC-29).
    handleIncomingNotification(data) {
      this.$store.dispatch("notificaciones/recibirNotificacionEnVivo", data);
    },
    deleteNotificacion(id) {
      this.$store.dispatch("notificaciones/eliminar", id).catch((error) => {
        console.error("Error al eliminar notificacion:", error);
      });
    },
    // Click en una notificación de la campanita: descarga el archivo adjunto
    // (p.ej. el Excel de un reporte async) o navega a la ruta interna del SPA,
    // según lo que indique notificacionAccion.abrirNotificacionUrl.
    descargarNotificacion(item) {
      abrirNotificacionUrl(item.url, this.$router);
    },
    ocultarBadge() {
      this.badgeDismissed = true;
    },
    formatDate(date) {
      const fecha = moment(date);
      return fecha.isSame(moment(), "day")
        ? fecha.format("HH:mm")
        : fecha.format("DD/MM HH:mm");
    },
  },
};
</script>


<style lang="scss" scoped>
/* Solo lo que Vuetify no cubre con utilidades. */
.notif-panel {
  width: 380px;
  max-width: calc(100vw - 32px);
}

.notif-list {
  max-height: 420px;
}

.notif-item__accent {
  flex: 0 0 3px;
}

/* Tinte del primario al 6% para las no leidas: se pinta como capa aparte
   porque Vuetify no expone tintes del color del tema, y el color de marca
   varia por cliente (nunca hex). */
.notif-item--unread {
  position: relative;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--v-primary-base);
    opacity: 0.06;
    z-index: -1;
  }
}
</style>
